import { useCallback } from 'react';

import { Dispatch } from '@reduxjs/toolkit';
import { randomUUID } from 'expo-crypto';
import { useStoreContext } from 'features/store';

import { ICartEntry } from '@rbi-ctg/menu';
import {
  IHydrateCartInput,
  IMenuSelectionInput,
  Platform,
  ServiceMode,
  useHydrateCartMutation,
} from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { validateAppliedReward } from 'state/global-state/models/loyalty/rewards/rewards.slice';
import { IMinimalCartEntry } from 'state/global-state/models/ordering/cart/cart.types';
import { useServiceModeContext } from 'state/service-mode';
import { createCartEntriesFromQuantity } from 'utils/cart';
import { isWeb } from 'utils/environment';
import {
  buildMinimalCartEntriesLookup,
  findMatchingCartEntry,
  findRewardBenefitFromHydratedCartEntry,
  mapHydratedCart,
  mapServiceModeToCartServiceMode,
} from 'utils/minimal-cart';

import { useCart } from './use-cart';

export interface IUseMinimalCart {
  cartEntries: IMinimalCartEntry[];
  addItemToMinimalCart: (cartEntry: IMinimalCartEntry) => void;
  emptyMinimalCart: () => void;
  hydrateMinimalCart: (entries?: IMinimalCartEntry[]) => Promise<boolean>;
}

/**
 * Expose the cart state and the utility methods to mutate it.
 *
 * When an action to mutate the cart is triggered, it manages the Redux cart actions, logs the events,
 * coordinates with other parts of the code, and dispatches actions on related slices.
 */
export const useMinimalCart = (): IUseMinimalCart => {
  const dispatch = useAppDispatch();
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const totalTimesRewardApplied = useAppSelector(selectors.loyalty.selectTotalTimesRewardApplied);
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { serviceMode } = useServiceModeContext();
  const { isAuthenticated } = useAuthContext();
  const [hydrateCart] = useHydrateCartMutation();
  const { addItemToCart, updateCartEntry } = useCart();

  // Adds a single cartEntry to the cart
  const addItemToMinimalCart = useCallback<IUseMinimalCart['addItemToMinimalCart']>(
    minimalCartEntry => {
      dispatch(actions.ordering.addMinimalCartEntry(minimalCartEntry));
    },
    [dispatch]
  );

  const emptyMinimalCart = useCallback<IUseMinimalCart['emptyMinimalCart']>(() => {
    dispatch(actions.ordering.resetMinimalCart());
  }, [dispatch]);

  const handleRewards = useCallback(
    (rewardBenefitId: string, cartEntry: ICartEntry, remainingPoints: { value: number }) => {
      let cartEntryWithReward = { ...cartEntry };

      const { isApplicable, mappedReward } = validateAppliedReward(
        availableLoyaltyRewardsMap,
        rewardBenefitId,
        totalTimesRewardApplied,
        1,
        remainingPoints.value
      );

      if (isApplicable) {
        dispatch(
          actions.loyalty.applyReward({
            rewardBenefitId,
            cartId: cartEntryWithReward.cartId,
            quantity: 1,
          })
        );
        remainingPoints.value -= mappedReward?.pointCost ?? 0;
      } else {
        const rewardBenefit = findRewardBenefitFromHydratedCartEntry(cartEntry);
        if (rewardBenefit) {
          cartEntryWithReward = { ...rewardBenefit };
          const newId = randomUUID();
          cartEntryWithReward.cartId = newId;
          // @ts-ignore
          cartEntryWithReward.lineId = newId;
        }
      }
      return cartEntryWithReward;
    },
    [availableLoyaltyRewardsMap, dispatch, totalTimesRewardApplied]
  );

  const hydrateMinimalCart = useCallback(
    async (entries?: IMinimalCartEntry[]) => {
      if (!storeId || !serviceMode || !isAuthenticated) {
        return false;
      }

      return hydrateEntries(
        entries ?? minimalCartEntries,
        storeId,
        serviceMode,
        hydrateCart,
        addItemToCart,
        updateCartEntry,
        dispatch,
        handleRewards,
        stagedCartPoints
      );
    },
    [
      addItemToCart,
      dispatch,
      handleRewards,
      hydrateCart,
      isAuthenticated,
      minimalCartEntries,
      serviceMode,
      stagedCartPoints,
      storeId,
      updateCartEntry,
    ]
  );

  return {
    cartEntries: minimalCartEntries,
    addItemToMinimalCart,
    emptyMinimalCart,
    hydrateMinimalCart,
  };
};

// Had to extract this to a pure function to avoid a circular dep between use-cart and use-minimal-cart
export async function hydrateEntries(
  cartEntries: IMinimalCartEntry[],
  storeId: string,
  serviceMode: ServiceMode,
  hydrateCart: ReturnType<typeof useHydrateCartMutation>[0],
  addItemToCart: ReturnType<typeof useCart>['addItemToCart'],
  updateCartEntry: ReturnType<typeof useCart>['updateCartEntry'],
  dispatch: Dispatch,
  handleRewards?: (
    rewardBenefitId: string,
    cartEntry: ICartEntry,
    remainingPoints: { value: number }
  ) => ICartEntry,
  stagedCartPoints: number = 0
): Promise<boolean> {
  const channel = isWeb ? Platform.WEB : Platform.APP;
  const menuSelections: IMenuSelectionInput[] = cartEntries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: entry.menuSelections,
  }));
  const input: IHydrateCartInput = {
    cart: { menuSelections },
    serviceMode: mapServiceModeToCartServiceMode(serviceMode),
    storeId,
  };

  try {
    const hydrationResult = await hydrateCart({
      variables: { channel, input },
    });

    if (hydrationResult.errors) {
      throw hydrationResult.errors;
    }

    const hydratedCartEntries = mapHydratedCart(
      hydrationResult?.data?.hydrateCart.cartEntries ?? [],
      buildMinimalCartEntriesLookup(cartEntries)
    );

    // Initialize remainingPoints outside the loop
    const remainingPoints = { value: stagedCartPoints };

    hydratedCartEntries.forEach(cartEntry => {
      const matchingMinimalCartEntry = findMatchingCartEntry(cartEntries, cartEntry);
      if (matchingMinimalCartEntry?.isEdit) {
        updateCartEntry(cartEntry, cartEntry.cartId, true, true);
      } else if (handleRewards && matchingMinimalCartEntry?.rewardBenefitId) {
        const rewardBenefitId = matchingMinimalCartEntry.rewardBenefitId;
        // For rewards, we need to create a set of cartEntries based on the quantity.
        // This will ensure we can run one cartEntry at a time to avoid having race condition issues.
        const cartEntriesFromQuantity = createCartEntriesFromQuantity(cartEntry);

        cartEntriesFromQuantity.forEach(cartEntry => {
          // This will create the applied reward for the cartEntry.
          handleRewards(rewardBenefitId, cartEntry, remainingPoints);
          addItemToCart(cartEntry, undefined, false, true);
        });
      } else {
        addItemToCart(cartEntry, undefined, false, true);
      }
    });
    dispatch(actions.ordering.resetMinimalCart());
    return true;
  } catch (error) {
    dispatch(actions.ordering.resetMinimalCart());
    return false;
  }
}
